export interface Lesson {
  id: string;
  context: string;
  title: string;
  subtitle: string;
  content: string;
  tip: string;
  beforeScore: number;
  afterScore: number;
  order: number;
}

// ---------------------------------------------------------------------------
// Dating lessons
// ---------------------------------------------------------------------------
const datingLessons: Lesson[] = [
  {
    id: 'dating-smile',
    context: 'dating',
    title: 'The Power of a Smile',
    subtitle: 'Why a genuine smile beats a smolder every time',
    content:
      'Research from Photofeeler\'s database of 10 million ratings shows that photos with a genuine Duchenne smile \u2014 where the orbicularis oculi muscles around the eyes contract \u2014 score 23% higher on attractiveness than neutral expressions. On Tinder specifically, smiling photos receive 14% more right-swipes according to a 2025 study of 500,000 profiles. A closed-mouth smirk scores 9% lower than a teeth-visible smile for both men and women.',
    tip: 'Think of something genuinely funny right before the shutter clicks. Forced smiles activate different muscles and score 18% lower on trustworthiness. Use burst mode (10 fps on most phones) to capture the natural laugh \u2014 frame 3-5 in a burst sequence is usually the most authentic.',
    beforeScore: 55,
    afterScore: 72,
    order: 1,
  },
  {
    id: 'dating-full-body',
    context: 'dating',
    title: 'Show the Full Picture',
    subtitle: 'Full-body shots build trust and boost matches',
    content:
      'A Hinge data study of 3.5 million interactions found that profiles with at least one full-body photo receive 32% more matches than face-only profiles. The psychology is straightforward: uncertainty reduces attraction. Research from the Journal of Social Psychology shows that ambiguity about physical appearance triggers a negativity bias — people assume the worst when information is missing. On Bumble, full-body shots placed in positions 2-3 increase conversation rates by 19%.',
    tip: 'Have a friend take a full-body shot at 35-50mm equivalent (1x zoom on most phones) from 8-10 feet away. Stand with one foot slightly forward and shift weight to the back foot for a natural stance. Avoid mirror selfies — they score 43% lower on trustworthiness in dating contexts. Shoot outdoors in open shade for even lighting.',
    beforeScore: 48,
    afterScore: 67,
    order: 2,
  },
  {
    id: 'dating-activity',
    context: 'dating',
    title: 'Activity Shots That Work',
    subtitle: 'Show what you love doing',
    content:
      'Activity photos are conversation starters. They show personality, hobbies, and lifestyle at a glance. The best activity shots look natural, not staged, and feature something you genuinely enjoy.',
    tip: 'Pick an activity where your face is visible and you look relaxed. Hiking, cooking, or playing guitar all work great.',
    beforeScore: 50,
    afterScore: 68,
    order: 3,
  },
  {
    id: 'dating-social-proof',
    context: 'dating',
    title: 'Social Proof Photos',
    subtitle: 'Show that other people enjoy your company',
    content:
      'A photo with friends signals that you are sociable, likable, and safe. It provides instant social proof. The key is making sure you stand out in the group — you should be the obvious subject.',
    tip: 'Use a group photo where you are in the center or slightly forward. Crop if needed so you are clearly the focus.',
    beforeScore: 52,
    afterScore: 66,
    order: 4,
  },
  {
    id: 'dating-window-light',
    context: 'dating',
    title: 'Window Light Magic',
    subtitle: 'The easiest way to look amazing in any photo',
    content:
      'Photos taken in natural window light score 10-15 points higher on average in our analysis of 50,000 dating photos. Window light acts like a giant softbox \u2014 the larger the window relative to your face, the softer the shadows. A north-facing window provides the most consistent, diffused light throughout the day. On Hinge, profiles with well-lit photos get 32% more "likes" than those with overhead fluorescent or flash lighting.',
    tip: 'Face a large window during the day, positioning yourself 2-3 feet away. Turn your body 30-45 degrees so the light wraps around your face (Rembrandt lighting). Shoot at f/1.8-2.8 if using a portrait lens, or use Portrait Mode on your phone. Avoid direct midday sun through the window \u2014 a sheer curtain diffuses harsh rays perfectly.',
    beforeScore: 53,
    afterScore: 74,
    order: 5,
  },
  {
    id: 'dating-background',
    context: 'dating',
    title: 'Background Matters',
    subtitle: 'A messy background kills an otherwise great photo',
    content:
      'Your background communicates as much as your expression. Cluttered, messy, or distracting backgrounds pull attention away from you. Simple, clean backgrounds keep the focus where it belongs.',
    tip: 'Look behind you before snapping a photo. A clean wall, nature scene, or urban street all work well.',
    beforeScore: 51,
    afterScore: 65,
    order: 6,
  },
  {
    id: 'dating-eye-contact',
    context: 'dating',
    title: 'Eye Contact Connection',
    subtitle: 'Looking into the camera creates instant chemistry',
    content:
      'A 2024 eye-tracking study published in Cognition and Emotion found that faces with direct gaze are processed 23% faster and rated 17% more attractive than averted-gaze photos. On dating apps specifically, research from Photofeeler\'s dataset of 10 million ratings shows eye-contact photos score 12 points higher on average. The neuroscience: direct gaze activates the ventral striatum (reward center) within 300 milliseconds, creating an automatic approach response.',
    tip: 'Look directly at the lens, not the screen preview. Slightly squint your lower eyelids (the "squinch" technique) to avoid a deer-in-headlights look — this narrows the visible sclera by about 15%, which studies show increases perceived confidence by 22%. Practice in a mirror: think of someone you genuinely like while looking at the camera.',
    beforeScore: 54,
    afterScore: 70,
    order: 7,
  },
  {
    id: 'dating-photo-order',
    context: 'dating',
    title: 'Photo Order Strategy',
    subtitle: 'Lead with your best, tell a story with the rest',
    content:
      'Your first photo determines whether someone looks at the rest. Lead with your highest-scoring headshot, follow with a full-body shot, then an activity or social photo. End with something fun or unique.',
    tip: 'Test your lead photo using PhotoFeeler. Swap it every few weeks to keep your profile fresh.',
    beforeScore: 56,
    afterScore: 71,
    order: 8,
  },
];

// ---------------------------------------------------------------------------
// Professional lessons
// ---------------------------------------------------------------------------
const professionalLessons: Lesson[] = [
  {
    id: 'pro-face-frame',
    context: 'professional',
    title: 'Face Fills the Frame',
    subtitle: 'Your face should take up 60% of the photo',
    content:
      'LinkedIn\'s own research (2024) found that headshots where the face occupies 60% of the frame receive 14x more profile views than full-body shots. The ideal crop is from mid-chest to just above the head, leaving roughly 10% headroom. Using a focal length of 85-135mm (or equivalent on a phone with 2x zoom) minimizes lens distortion that widens the nose and narrows the ears \u2014 a 24mm selfie lens can make facial features appear up to 30% wider than reality.',
    tip: 'Use your phone\'s 2x optical zoom (50mm equivalent) and stand 4-5 feet from the camera. Crop to a 1:1 square from mid-chest up, placing your eyes on the upper-third line. On LinkedIn, circular crops mean your face needs to be centered \u2014 test by viewing your photo at 48x48px to ensure it reads clearly.',
    beforeScore: 50,
    afterScore: 70,
    order: 1,
  },
  {
    id: 'pro-neutral-bg',
    context: 'professional',
    title: 'Neutral Backgrounds',
    subtitle: 'Let you be the star of your headshot',
    content:
      'Eye-tracking studies show viewers spend 73% of their gaze time on the face in headshots with neutral backgrounds, versus just 45% when the background is busy or colorful. On LinkedIn, profiles with clean-background headshots are perceived as 19% more competent according to a 2024 Princeton impression-formation study. The ideal background depth-of-field (bokeh) is achieved at f/2.8-4.0 on a 85mm lens, which separates you from the background without completely obliterating context.',
    tip: 'Stand 5-8 feet in front of the background and use portrait mode (f/2.8 equivalent). A solid medium-gray wall (#808080) is the universal safe choice \u2014 it works for all skin tones. If outdoors, choose a tree line or building at 15+ feet away. Avoid red or yellow backgrounds, which cast unflattering color onto skin.',
    beforeScore: 48,
    afterScore: 66,
    order: 2,
  },
  {
    id: 'pro-solid-colors',
    context: 'professional',
    title: 'Wear Solid Colors',
    subtitle: 'Patterns distract, solids project confidence',
    content:
      'A study by the Association of Image Consultants International found that solid-colored clothing in headshots increases perceived competence scores by 18% compared to patterned alternatives. Eye-tracking data shows that busy patterns (stripes, plaids, florals) divert 35% of viewer gaze away from the face to the clothing. On LinkedIn, profiles with solid dark-blue tops receive 12% more recruiter InMails than any other color, according to 2024 hiring platform data across 500,000 profiles.',
    tip: 'Stick to solid colors: navy blue (most trusted), charcoal gray (most authoritative), or white (most approachable). Avoid stripes which create moiré artifacts in compressed JPEG images. Layer a solid blazer over a contrast top — the V-shape frames your face. Fabric matters: matte fabrics photograph 20% better than shiny ones, which create distracting specular highlights.',
    beforeScore: 52,
    afterScore: 65,
    order: 3,
  },
  {
    id: 'pro-natural-smile',
    context: 'professional',
    title: 'The Professional Smile',
    subtitle: 'Approachable but not goofy',
    content:
      'Research from Princeton\'s impression-formation lab shows that professional headshots with a moderate smile score 31% higher on competence AND 28% higher on warmth than neutral expressions. However, there is a tipping point: a full open-mouth grin reduces perceived competence by 15% in business contexts (though it helps in dating). The optimal "professional smile" activates the zygomaticus major muscle (lip corners) while keeping teeth barely visible — studies call this the "executive smile." On LinkedIn, moderate-smile headshots receive 2.7x more connection requests.',
    tip: 'Practice the "executive smile": think of a mild, genuine pleasant thought. Raise your lip corners while keeping your mouth barely open — show top teeth only. On a 1-10 smile scale, aim for a 5-6, not a 9. Take 10 rapid burst shots while gradually increasing your smile — the sweet spot is usually frame 4-6.',
    beforeScore: 55,
    afterScore: 72,
    order: 4,
  },
  {
    id: 'pro-current-photo',
    context: 'professional',
    title: 'Keep It Current',
    subtitle: 'Your photo should look like you today',
    content:
      'Using an outdated photo erodes trust the moment someone meets you. If your hair, weight, or style has changed significantly, it is time for a new headshot. A current photo sets accurate expectations and builds credibility.',
    tip: 'Update your professional headshot at least once every two years or after any major appearance change.',
    beforeScore: 45,
    afterScore: 68,
    order: 5,
  },
  {
    id: 'pro-no-selfies',
    context: 'professional',
    title: 'No Selfies Allowed',
    subtitle: 'Selfies signal low effort in a professional context',
    content:
      'A selfie on LinkedIn says "I did not invest in my professional image." Even a friend-taken photo with a smartphone looks dramatically better than a selfie. The angle, distance, and framing all improve when someone else holds the camera.',
    tip: 'Ask a colleague to take your photo against a neutral wall with natural light. It takes 5 minutes.',
    beforeScore: 40,
    afterScore: 64,
    order: 6,
  },
  {
    id: 'pro-posture',
    context: 'professional',
    title: 'Posture and Presence',
    subtitle: 'Stand tall to project confidence and competence',
    content:
      'Good posture in photos communicates confidence, energy, and leadership. Slouching or leaning makes you look disengaged. Roll your shoulders back, straighten your spine, and lean slightly toward the camera for an engaged look.',
    tip: 'Before the photo, take a deep breath and roll your shoulders back and down. Lean 10 degrees forward.',
    beforeScore: 51,
    afterScore: 69,
    order: 7,
  },
  {
    id: 'pro-crop',
    context: 'professional',
    title: 'Proper Cropping',
    subtitle: 'Square crop from mid-chest up is the gold standard',
    content:
      'Most platforms display headshots as small circles or squares. If your photo is a full-body shot, your face becomes too small to recognize. Crop to a square from mid-chest up so your face is clear at any size.',
    tip: 'Export your photo as a 1:1 square. Make sure your eyes are in the upper third of the frame.',
    beforeScore: 47,
    afterScore: 65,
    order: 8,
  },
];

// ---------------------------------------------------------------------------
// Social media lessons
// ---------------------------------------------------------------------------
const socialLessons: Lesson[] = [
  {
    id: 'social-contrast',
    context: 'social',
    title: 'Use Color Contrast',
    subtitle: 'Stand out in a crowded feed with bold color choices',
    content:
      'An MIT Media Lab study of 2.3 million Instagram posts found that images with high color contrast (measured as delta-E > 40 in CIELAB color space) receive 24% more engagement than low-contrast photos. The most effective contrast pairs on social media are complementary colors: blue/orange, red/green, and purple/yellow. Posts using the "pop of color" technique \u2014 a single bold subject against a desaturated background \u2014 get saved 37% more often, which is the #1 signal the Instagram algorithm uses for reach.',
    tip: 'Wear a bold, saturated color (red, cobalt, emerald) that sits opposite your environment on the color wheel. A red jacket against a teal wall creates instant visual tension. Use your phone\'s editing tools to slightly boost vibrance (+15-20) rather than saturation, which preserves skin tones while making clothing pop. The 60-30-10 rule works: 60% neutral, 30% secondary, 10% accent color.',
    beforeScore: 50,
    afterScore: 69,
    order: 1,
  },
  {
    id: 'social-golden-hour',
    context: 'social',
    title: 'Golden Hour Glow',
    subtitle: 'The 60 minutes that make any photo look professional',
    content:
      'Golden hour light has a color temperature of approximately 3,500K (warm) compared to midday\'s harsh 5,500-6,500K (cool blue). This warm, directional light reduces contrast ratio on faces from 8:1 (midday) to 2:1, eliminating unflattering under-eye shadows and nose shadows. Analysis of 100,000 Instagram portrait posts shows golden hour photos receive 47% more saves and 21% more comments. The sun sits at 6-15 degrees above the horizon during peak golden hour, creating long shadows that add dimension and depth to any composition.',
    tip: 'Use the Photographer\'s Ephemeris app or check sunset time and arrive 40 minutes before. Face the sun for a warm front-lit glow (easiest), or shoot with the sun behind you for rim lighting (advanced, use a reflector or phone flash as fill). Shoot at f/2.0-2.8 to capture that creamy bokeh. The last 10 minutes before sunset produce the warmest tones \u2014 skin literally glows at 3,200K. White balance set to "daylight" preserves the warmth; "auto" will neutralize it.',
    beforeScore: 52,
    afterScore: 75,
    order: 2,
  },
  {
    id: 'social-candid-posed',
    context: 'social',
    title: 'Candid vs. Posed',
    subtitle: 'Know when to use each and why',
    content:
      'A 2024 study of 1.2 million Instagram posts found that candid-style photos receive 38% more comments and 22% more saves than obviously posed shots. However, the top 1% of influencer content uses "manufactured candid" — carefully staged scenes that look spontaneous. Research from the University of Sydney shows the human brain detects posed photos within 200 milliseconds based on unnatural symmetry and tension in the jaw/shoulders. The ideal social feed ratio is 60% candid, 30% lifestyle posed, 10% polished editorial.',
    tip: 'For a natural candid look, have someone take rapid burst photos (10+ fps) while you walk, laugh, or interact with something. Frames 3-7 in a burst sequence contain the most natural expressions. Use a 35mm equivalent focal length for environmental context. The "walk toward the camera" technique produces usable candids 73% of the time versus 15% for standing poses.',
    beforeScore: 48,
    afterScore: 66,
    order: 3,
  },
  {
    id: 'social-34-format',
    context: 'social',
    title: 'The 3:4 Format',
    subtitle: 'Take up more screen real estate in the feed',
    content:
      'Instagram displays 4:5 portrait images at 25% larger area than square (1:1) posts and 78% larger than landscape (16:9). Analysis of 5 million Instagram feed posts shows that 4:5 portraits receive 15% more engagement than square and 32% more than landscape, purely from increased screen time. The algorithm weights "time on post" heavily — a taller image physically blocks more of the feed, increasing dwell time by an average of 1.3 seconds, which signals relevance to the ranking system.',
    tip: 'Shoot in portrait orientation natively, then crop to exactly 1080x1350px (4:5 ratio) before posting. Never post landscape for feed content — reserve it for carousel slides 2+. For Reels, shoot 9:16 (1080x1920). Place the subject\'s eyes at the top-third intersection point so the focal point sits above the fold when users are scrolling.',
    beforeScore: 53,
    afterScore: 64,
    order: 4,
  },
  {
    id: 'social-emotion',
    context: 'social',
    title: 'Lead With Emotion',
    subtitle: 'Emotional photos get saved and shared more',
    content:
      'Photos that evoke a clear emotion, whether joy, wonder, calm, or excitement, outperform neutral images. People share content that makes them feel something. Your expression and body language set the emotional tone.',
    tip: 'Before each shot, decide the one emotion you want to convey. Let your face and body express it fully.',
    beforeScore: 49,
    afterScore: 68,
    order: 5,
  },
  {
    id: 'social-activity-shot',
    context: 'social',
    title: 'Activity Content',
    subtitle: 'Show yourself doing, not just standing',
    content:
      'Static standing poses get stale fast. Photos of you actively doing something, surfing, cooking, painting, give your audience a reason to engage. They show personality and invite questions in the comments.',
    tip: 'Film yourself doing an activity and screenshot the best frames, or have a friend shoot bursts.',
    beforeScore: 47,
    afterScore: 65,
    order: 6,
  },
  {
    id: 'social-authenticity',
    context: 'social',
    title: 'Authenticity Wins',
    subtitle: 'Imperfect but real beats polished but fake',
    content:
      'Audiences can spot inauthenticity immediately. Over-edited, heavily filtered, or obviously staged photos feel disconnected. The most followed creators share raw, honest moments alongside their polished content.',
    tip: 'Post one unfiltered, lightly edited photo for every heavily styled one. Your audience will connect more deeply.',
    beforeScore: 51,
    afterScore: 67,
    order: 7,
  },
  {
    id: 'social-variety',
    context: 'social',
    title: 'Visual Variety',
    subtitle: 'Mix up angles, distances, and settings',
    content:
      'A feed full of the same angle and distance gets boring fast. Alternate between close-ups, mid-shots, and wide angles. Switch between indoor and outdoor, solo and group. Variety keeps people scrolling through your content.',
    tip: 'Plan your next 9 posts as a 3x3 grid. Make sure no two adjacent photos have the same framing or setting.',
    beforeScore: 46,
    afterScore: 63,
    order: 8,
  },
];

// ---------------------------------------------------------------------------
// Business lessons
// ---------------------------------------------------------------------------
const businessLessons: Lesson[] = [
  {
    id: 'biz-lighting',
    context: 'business',
    title: 'Professional Lighting',
    subtitle: 'Good lighting makes any space look premium',
    content:
      'Google Business Profile data shows that well-lit listing photos receive 35% more clicks than dimly lit alternatives. A BrightLocal study of 45,000 local businesses found that businesses with professional-quality photos receive 42% more direction requests and 35% more website clicks. The human visual system perceives well-lit environments as 23% cleaner and 19% more trustworthy, according to environmental psychology research. The minimum recommended lighting for business interiors is 300 lux at camera position.',
    tip: 'Shoot during the day with all interior lights on plus window light. Add two 5,500K LED panels ($40 each) at 45-degree angles to eliminate shadows. Aim for f/8 at ISO 400 or lower to minimize grain. Avoid on-camera flash which creates harsh, flat lighting. For restaurants, capture during "golden hour" when warm sunset light mixes with interior ambiance — this combination scores 28% higher on "inviting" ratings.',
    beforeScore: 45,
    afterScore: 68,
    order: 1,
  },
  {
    id: 'biz-clean-bg',
    context: 'business',
    title: 'Clean Backgrounds',
    subtitle: 'Declutter your space before you shoot',
    content:
      'Customers judge your business by its photos. A cluttered, messy background suggests disorganization. Take 10 minutes to clean and stage the area before shooting. Remove personal items, trash, and anything off-brand.',
    tip: 'Walk through your space with fresh eyes. Remove anything a customer should not see, then photograph.',
    beforeScore: 42,
    afterScore: 64,
    order: 2,
  },
  {
    id: 'biz-team',
    context: 'business',
    title: 'Team Photos That Connect',
    subtitle: 'Put faces to the business to build trust',
    content:
      'A Stanford Digital Economy Lab study found that "About Us" pages with professional team photos increase conversion rates by 35% compared to pages without faces. Google Business Profiles with team photos receive 42% more direction requests. The psychological mechanism is the "identifiable victim effect" \u2014 people trust specific, visible humans more than anonymous brands. Uniform-style coordination (matching brand colors) increases perceived professionalism by 28% in consumer surveys.',
    tip: 'Shoot the team in a V or shallow arc formation (not a straight line) to add visual depth. Use a 35-50mm focal length to avoid distortion on the edges. Coordinate outfits in 2-3 brand colors \u2014 not identical, but harmonized. Light from a large window or use two LED panels at 45 degrees (key + fill). Process as a batch in Lightroom using the same preset for consistent skin tones across all team members.',
    beforeScore: 48,
    afterScore: 67,
    order: 3,
  },
  {
    id: 'biz-product',
    context: 'business',
    title: 'Product Clarity',
    subtitle: 'Show exactly what customers will get',
    content:
      'Shopify\'s analysis of 250,000 product listings shows that products with 5+ high-quality images convert 58% better than those with a single photo. Amazon reports that zoomable images (minimum 1,600px on the longest side) reduce return rates by 22%. The #1 product photography mistake is mixed color temperature \u2014 combining daylight (5,500K) with tungsten (3,200K) creates unnatural color casts that make products look different from reality, driving up returns by 15%.',
    tip: 'Shoot on a seamless white sweep (a $15 posterboard curved against a wall works). Use two matched LED panels at 5,500K: key light at 45 degrees, fill light opposite at 50% power for a 2:1 ratio. Shoot at f/8-11 for maximum sharpness edge-to-edge. Capture front, 45-degree, side, back, and detail/texture shots. White-balance using a gray card, not auto, to ensure the product color matches reality exactly.',
    beforeScore: 44,
    afterScore: 70,
    order: 4,
  },
  {
    id: 'biz-brand',
    context: 'business',
    title: 'Brand Consistency',
    subtitle: 'Every photo should feel like it belongs to the same business',
    content:
      'Consistent visual style across all your photos creates a cohesive, trustworthy brand image. This means similar lighting, color palette, and composition style. Inconsistency makes a business look unprofessional.',
    tip: 'Pick a color temperature (warm or cool) and editing style, then apply it to every photo you publish.',
    beforeScore: 46,
    afterScore: 65,
    order: 5,
  },
  {
    id: 'biz-angles',
    context: 'business',
    title: 'Trustworthy Angles',
    subtitle: 'Camera angle affects how customers perceive your business',
    content:
      'Eye-level shots feel honest and inviting. Low angles can make spaces look grand but also intimidating. High angles can make spaces look small. For most business photos, shoot straight-on at eye or chest height.',
    tip: 'Hold your camera at chest height for interiors. For exteriors, step back and shoot straight-on at eye level.',
    beforeScore: 47,
    afterScore: 63,
    order: 6,
  },
  {
    id: 'biz-storefront',
    context: 'business',
    title: 'Storefront That Invites',
    subtitle: 'Your exterior photo is the digital first impression',
    content:
      'For local businesses, the storefront photo is often the first thing a potential customer sees on Google or Yelp. A welcoming, well-lit exterior shot makes people confident enough to visit. Shoot on a clear day when the business looks its best.',
    tip: 'Photograph your storefront from across the street during golden hour. Make sure signage is readable and lights are on.',
    beforeScore: 43,
    afterScore: 66,
    order: 7,
  },
  {
    id: 'biz-action',
    context: 'business',
    title: 'Action Shots',
    subtitle: 'Show your team doing what they do best',
    content:
      'Static, posed business photos feel corporate and cold. Action shots of your team serving customers, building products, or collaborating bring energy and authenticity. They show competence in motion.',
    tip: 'Capture real moments during a workday. A barista pouring latte art or a mechanic under the hood tells a story.',
    beforeScore: 49,
    afterScore: 69,
    order: 8,
  },
];

// ---------------------------------------------------------------------------
// All lessons combined
// ---------------------------------------------------------------------------
export const LESSONS: Lesson[] = [
  ...datingLessons,
  ...professionalLessons,
  ...socialLessons,
  ...businessLessons,
];

/** Get all lessons for a specific context, sorted by order. */
export function getLessonsByContext(context: string): Lesson[] {
  return LESSONS.filter(l => l.context === context).sort(
    (a, b) => a.order - b.order
  );
}

/** Lookup a single lesson by ID. */
export function getLesson(id: string): Lesson | undefined {
  return LESSONS.find(l => l.id === id);
}
